import React from 'react';
import { AppShell } from '../components/Layout';
import SeoHead from '../components/seo/SeoHead';

/**
 * TermsPage — plain-English Terms of Service.
 *
 * NOTE: This is a starter draft. Please replace with the final terms
 * reviewed by your legal counsel before production launch.
 */
export default function TermsPage() {
  return (
    <AppShell>
      <SeoHead
        title={"Terms of Service — Blayzrr"} 
        description={"Blayzrr terms of service, acceptable use, and account policies."} 
        path={"/terms"} 
      />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20" data-testid="terms-page">
        <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-primary">Legal</p>
        <h1 className="mt-3 font-display font-semibold text-4xl sm:text-5xl tracking-[-0.04em]">
          Terms of Service
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Last updated: 28 June 2026
        </p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-foreground/90">
          <Section title="1. Who we are">
            Blayzrr (“we”, “us”) is a LinkedIn auditing and voice-matched content platform operated for individual professionals. By creating an account at blayzrr.com you agree to these terms.
          </Section>
          <Section title="2. What you get">
            Paid plans grant access to: profile audits, voice-matched rewrites, researched posts, PoV posts, prompt drafts, premium carousels, and (on Enterprise*) the inspiration / benchmark tab &mdash; subject to the quotas published on the Pricing page. We do not offer a free plan or free full audit; the on-page score is a preview.
          </Section>
          <Section title="3. Acceptable use">
            You will not (a) automate posting through unauthorised channels, (b) generate content that violates LinkedIn’s Professional Community Policies, (c) attempt to reverse-engineer our voice-match models, or (d) misrepresent your identity in audits.
          </Section>
          <Section title="4. Cancellation">
            The monthly Executive Thought Leadership Plan is month-to-month with no auto-pay surprises. Cancellations take effect at the end of the current billing cycle and stop further charges. See the Refund Policy for refund conditions.
          </Section>
          <Section title="5. Ownership of outputs">
            The drafts, rewrites, and carousels generated for your account are yours. You retain the copyright on outputs you publish. We retain ownership of the underlying software, models, and templates.
          </Section>
          <Section title="6. Account responsibility">
            You are responsible for keeping your login credentials safe. We will never auto-post to your LinkedIn without explicit per-action approval.
          </Section>
          <Section title="7. Service availability">
            We aim for 99.5% monthly uptime but the service is provided on an “as-is” basis. Scheduled maintenance windows are communicated in advance.
          </Section>
          <Section title="8. Limitation of liability">
            To the maximum extent permitted by law, our aggregate liability for any claim arising from your use of the service is capped at the amount you paid us in the preceding three months.
          </Section>
          <Section title="9. Changes to these terms">
            We may update these terms; material changes will be notified via email and in-app banner at least 14 days before they take effect. Continued use after that date constitutes acceptance.
          </Section>
          <Section title="10. Contact">
            Questions? Email <a className="text-primary hover:underline" href="mailto:help@blayzrr.com">help@blayzrr.com</a>.
          </Section>
        </div>
      </main>
    </AppShell>
  );
}

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-display font-semibold text-xl sm:text-2xl tracking-[-0.02em]">
      {title}
    </h2>
    <div className="mt-3 text-foreground/85">{children}</div>
  </section>
);
